import React from 'react';

import type { ClonotypeRow } from '../types/antibodyTable';
import { getLiabilityBadge } from '../utilities/liabilityBadge';

interface UseVisibleRowsParams {
  tableData: ClonotypeRow[];
  hideSingletons: boolean;
  searchQuery: string;
  /** Effective filter — may be 'selected' when driven by activeExpand, not just sequenceFilter state */
  sequenceFilter: 'all' | 'unselected' | 'selected';
  selectedSequences: Set<string>;
}

/**
 * Applies the three-stage visibility pipeline for the outer clonotype table:
 *   1. Singleton filter — hides clonotypes with only 1 cell if hideSingletons is on
 *   2. Global search — matches across 11+ clonotype-level and sequence-level fields
 *   3. Sequence filter — 'unselected': clonotypes with ≥1 unselected seq; 'selected': clonotypes with ≥1 selected seq
 */
export function useVisibleRows({ tableData, hideSingletons, searchQuery, sequenceFilter, selectedSequences }: UseVisibleRowsParams): ClonotypeRow[] {
  return React.useMemo(() => {
    let rows = tableData;

    // 1. Hide singletons
    if (hideSingletons) {
      rows = rows.filter((row) => row.clonotypeSize > 1);
    }

    // 2. Global search across clonotype-level and sequence-level fields
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      rows = rows.filter((row) => {
        // Clonotype-level fields
        // clonotypeId: substring so "1" finds clonotypes 1, 10, 15, etc.
        // clonotypeSize / numberOfNodes: exact match — "10" finds size=10, not size=100.
        if (
          row.clonotypeId.toString().includes(q) ||
          row.clonotypeSize.toString() === q ||
          row.numberOfNodes.toString() === q ||
          row.vjGeneHeavy.toLowerCase().includes(q) ||
          row.vjGeneLight.toLowerCase().includes(q) ||
          row.representativeCDRH3.toLowerCase().includes(q) ||
          row.representativeCDRL3.toLowerCase().includes(q) ||
          (row.igIsotype ?? '').toLowerCase().includes(q)
        ) {
          return true;
        }
        // Sequence-level fields
        return row.sequences.some((seq) => {
          const nodeIdMatch = seq.metadata.sequenceId.toLowerCase().includes(q);
          const barcodeMatch = seq.metadata.barcodes.some((b) => b.toLowerCase().includes(q));
          // nodeSize: exact match like SHMs — "10" finds nodeSize=10, not nodeSize=100.
          const nodeSizeMatch = seq.metadata.barcodes.length.toString() === q;
          const heavyMatch = seq.heavyChain?.sequence?.toLowerCase().includes(q) ?? false;
          const lightMatch = seq.lightChain?.sequence?.toLowerCase().includes(q) ?? false;
          // Use the same badge computation the UI renders — getLiabilityBadge picks the worst severity
          // across both chains (Critical > Strong > Mild > None/Weak).
          const liabilityStatusMatch = getLiabilityBadge(seq).label.toLowerCase().includes(q);
          const heavyRegionMatch = seq.heavyChain ? Object.values(seq.heavyChain.regions).some((v) => v?.toLowerCase().includes(q)) : false;
          const lightRegionMatch = seq.lightChain ? Object.values(seq.lightChain.regions).some((v) => v?.toLowerCase().includes(q)) : false;
          // APS: substring match works well for decimals ("2.5" matches "2.50")
          const apsMatch = seq.metadata.aps != null && seq.metadata.aps.toFixed(2).includes(q);
          // SHMs: exact match only — substring would make "10" hit shms=100, shms=210, etc.
          const shmsMatch = seq.metadata.shms != null && seq.metadata.shms.toString() === q;
          return (
            nodeIdMatch ||
            barcodeMatch ||
            nodeSizeMatch ||
            heavyMatch ||
            lightMatch ||
            liabilityStatusMatch ||
            heavyRegionMatch ||
            lightRegionMatch ||
            apsMatch ||
            shmsMatch
          );
        });
      });
    }

    // 3. Sequence filter — keep clonotypes that have at least one sequence matching the active filter
    if (sequenceFilter === 'unselected') {
      rows = rows.filter((row) => row.sequences.some((seq) => !selectedSequences.has(`${row.clonotypeId}-${seq.metadata.sequenceId}`)));
    } else if (sequenceFilter === 'selected') {
      rows = rows.filter((row) => row.sequences.some((seq) => selectedSequences.has(`${row.clonotypeId}-${seq.metadata.sequenceId}`)));
    }

    return rows;
  }, [tableData, hideSingletons, searchQuery, sequenceFilter, selectedSequences]);
}
